import React, { FC } from 'react'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'

import { EuiText, EuiTitle, EuiHorizontalRule, EuiCode, EuiCodeBlock, EuiLink, EuiSpacer } from '@elastic/eui'

export interface MarkdownRendererProps {
  content: string
  className?: string
}

const BlockquoteContext = React.createContext(false)

const ParagraphComponent = ({ children }: any) => {
  const isInBlockquote = React.useContext(BlockquoteContext)

  if (isInBlockquote) {
    return (
      <EuiText>
        <p>{children}</p>
      </EuiText>
    )
  }

  return (
    <>
      <EuiText>
        <p>{children}</p>
      </EuiText>
      <EuiSpacer size='m' />
    </>
  )
}

export const markdownComponents = {
  h1: ({ children }: any) => (
    <>
      <EuiTitle size='l'>
        <h1>{children}</h1>
      </EuiTitle>
      <EuiSpacer size='xl' />
    </>
  ),
  h2: ({ children }: any) => (
    <>
      <EuiTitle size='m'>
        <h2>{children}</h2>
      </EuiTitle>
      <EuiHorizontalRule />
    </>
  ),
  h3: ({ children }: any) => (
    <>
      <EuiTitle size='s'>
        <h3>{children}</h3>
      </EuiTitle>
      <EuiSpacer size='m' />
    </>
  ),
  h4: ({ children }: any) => (
    <>
      <EuiTitle size='xs'>
        <h4>{children}</h4>
      </EuiTitle>
      <EuiSpacer size='m' />
    </>
  ),
  h5: ({ children }: any) => (
    <>
      <EuiTitle size='xxs'>
        <h5>{children}</h5>
      </EuiTitle>
      <EuiSpacer size='m' />
    </>
  ),
  h6: ({ children }: any) => (
    <>
      <EuiTitle size='xxxs'>
        <h6>{children}</h6>
      </EuiTitle>
      <EuiSpacer size='m' />
    </>
  ),
  p: ParagraphComponent,
  ol: ({ children }: any) => (
    <>
      <EuiText>
        <ol style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>{children}</ol>
      </EuiText>
    </>
  ),
  ul: ({ children }: any) => (
    <>
      <EuiText>
        <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem' }}>{children}</ul>
      </EuiText>
      <EuiSpacer size='m' />
    </>
  ),
  li: ({ children }: any) => (
    <>
      <EuiText>
        <li style={{ marginBottom: '0.25rem' }}>{children}</li>
      </EuiText>
      <EuiSpacer size='s' />
    </>
  ),
  blockquote: ({ children }: any) => (
    <>
      <EuiText color='subdued'>
        <blockquote
          style={{
            borderLeft: '4px solid #d3dae6',
            paddingLeft: '1rem',
            margin: '1rem 0',
            fontStyle: 'italic'
          }}>
          <BlockquoteContext.Provider value={true}>{children}</BlockquoteContext.Provider>
        </blockquote>
      </EuiText>
      <EuiSpacer size='xxl' />
    </>
  ),
  code: ({ children, className }: any) => {
    const match = /language-(\w+)/.exec(className || '')
    const language = match ? match[1] : ''

    if (language) {
      return (
        <EuiCodeBlock language={language} paddingSize='m'>
          {children}
        </EuiCodeBlock>
      )
    }

    return <EuiCode>{children}</EuiCode>
  },
  pre: ({ children }: any) => <EuiCodeBlock paddingSize='m'>{children}</EuiCodeBlock>,
  a: ({ href, children }: any) => (
    <EuiLink href={href} target='_blank' external>
      {children}
    </EuiLink>
  ),
  hr: () => <EuiHorizontalRule style={{ height: '.5rem', borderRadius: '7px' }} />,
  strong: ({ children }: any) => <strong>{children}</strong>,
  em: ({ children }: any) => <em>{children}</em>,
  table: ({ children }: any) => (
    <>
      <EuiText>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #d3dae6',
            marginBottom: '1rem'
          }}>
          {children}
        </table>
      </EuiText>
      <EuiSpacer size='m' />
    </>
  ),
  thead: ({ children }: any) => <thead style={{ backgroundColor: '#f5f7fa' }}>{children}</thead>,
  th: ({ children }: any) => (
    <th
      style={{
        padding: '0.75rem',
        border: '1px solid #d3dae6',
        fontWeight: 600,
        textAlign: 'left'
      }}>
      {children}
    </th>
  ),
  td: ({ children }: any) => (
    <td
      style={{
        padding: '0.75rem',
        border: '1px solid #d3dae6'
      }}>
      {children}
    </td>
  )
}

const MarkdownRenderer: FC<MarkdownRendererProps> = ({ content, className }) => {
  return (
    <div className={className}>
      <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw, rehypeSanitize]} components={markdownComponents}>
        {content}
      </Markdown>
    </div>
  )
}

export default MarkdownRenderer
